import { Dropdown, Space, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Subscription, SubscriptionStatus } from '../../utils/types';
import { useSubscription } from '../../hooks/payment';
import { useAppContext } from '../../context/AppContext';
import { downloadFile } from '../../api';
import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { InfoCircleOutlined, MoreOutlined } from '@ant-design/icons';
import StatusTag from '../Properties/components/StatusTag';
import { Menu } from 'antd/lib';
import CancelSubscriptionModal from '../../components/CancelSubscriptionModal';
import ChoiceDropdown from '../../components/ChoiceDropdown';

export function donwloadSubscription(
  url: string,
  onProgress?: (progress: number) => void,
) {
  return downloadFile(url, 'subscription.pdf', onProgress);
}

export const formatDate = (date: Date) => {
  return dayjs(date).format('DD MMM YYYY');
};

const SUBSCRIPTION_STATUS_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Future', value: 'future' },
  { label: 'In Trial', value: 'in_trial' },
  { label: 'Cancelled', value: 'cancelled' },
];

const getColumns = (
  setSelectedSiteForCancellation: (siteId: number) => void,
  setIsCancelSubscriptionModalOpen: (visible: boolean) => void,
): ColumnsType<Subscription> => [
  {
    title: 'Domain/Property Name',
    dataIndex: 'domain',
    key: 'domainProperty',
    width: 200,
  },
  {
    title: 'Subscription Start Date',
    dataIndex: 'subscriptionCreatedDate',
    key: 'subscriptionCreatedDate',
    width: 200,
    render: (text: string) => {
      const date = new Date(text);
      // Check if text is a valid date, otherwise return text
      return isNaN(date.getTime()) ? (
        <span>{text}</span>
      ) : (
        <span>{formatDate(date)}</span>
      );
    },
  },
  {
    title: 'Next Billing Date',
    dataIndex: 'nextBillingDate',
    key: 'nextBillingDate',
    width: 200,
    render: (text: string) => {
      const date = new Date(text);
      // Check if text is a valid date, otherwise return text
      return isNaN(date.getTime()) ? (
        <span>{text}</span>
      ) : (
        <span>{formatDate(date)}</span>
      );
    },
  },
  {
    title: 'Amount',
    dataIndex: 'amount',
    key: 'amount',
    width: 200,
  },
  // {
  //   title: 'Payment Method',
  //   dataIndex: 'cardLast4',
  //   key: 'cardLast4',
  //   width: 200,
  // },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    width: 120,
    render: (status: string, record: Subscription) => (
      <Space direction="horizontal">
        <StatusTag status={status?.toLocaleLowerCase() as SubscriptionStatus} />
        {['cancelled'].includes(status?.toLowerCase() ?? '') && (
          <span style={{ verticalAlign: 'middle' }}>
            <Tooltip
              title={record?.cancellationReason || 'Unknown reason'}
              mouseEnterDelay={0.1}
            >
              <InfoCircleOutlined style={{ color: 'red' }} />
            </Tooltip>
          </span>
        )}
      </Space>
    ),
  },
  {
    title: 'Actions',
    key: 'actions',
    width: 120,
    render: (_, record) => {
      if (!['active', 'future'].includes(record.status?.toLowerCase?.())) {
        return null;
      }
      const menu = (
        <Menu>
          <Menu.Item
            key="cancel-subscription"
            onClick={() => {
              // TODO: Check after backend adds record.siteId to the response
              setSelectedSiteForCancellation(Number(record.siteId));
              setIsCancelSubscriptionModalOpen(true);
            }}
          >
            Cancel Subscription
          </Menu.Item>
        </Menu>
      );
      return (
        <Dropdown overlay={menu} trigger={['click']}>
          <MoreOutlined style={{ cursor: 'pointer' }} />
        </Dropdown>
      );
    },
  },
];

const Subscriptions = () => {
  const { pCode } = useAppContext();
  const { data: subscription, isPending, refetch } = useSubscription(pCode);
  const [isCancelSubscriptionModalOpen, setIsCancelSubscriptionModalOpen] =
    useState(false);
  const [selectedSiteForCancellation, setSelectedSiteForCancellation] =
    useState<number | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>(undefined);

  const columns = useMemo(
    () =>
      getColumns(
        setSelectedSiteForCancellation,
        setIsCancelSubscriptionModalOpen,
      ),
    [setSelectedSiteForCancellation, setIsCancelSubscriptionModalOpen],
  );

  const filteredDataSource = useMemo(() => {
    const data = Array.isArray(subscription) ? subscription : [];
    if (!selectedStatus || typeof selectedStatus !== 'string' || selectedStatus.trim() === '') {
      return data;
    }
    // Normalize selected status to lowercase
    const normalizedSelectedStatus = selectedStatus.toLowerCase().trim();
    
    return data.filter((sub: Subscription) => {
      // Skip if subscription status is missing or not a string
      if (!sub.status || typeof sub.status !== 'string') {
        return false;
      }
      const normalizedStatus = sub.status.toLowerCase().trim();
      // Only include if the subscription status exactly matches the selected status
      return normalizedStatus === normalizedSelectedStatus;
    });
  }, [subscription, selectedStatus]);

  const handleStatusChange = (value: string | undefined) => {
    setSelectedStatus(value);
  };

  return (
    <>
      <div style={{ marginBottom: '16px' }}>
        <ChoiceDropdown
          options={SUBSCRIPTION_STATUS_OPTIONS}
          onChange={handleStatusChange}
          placeholder="Filter by Status"
          mode={undefined}
          data-testid="dropdown-select"
        />
      </div>
      <Table
        columns={columns}
        dataSource={Array.isArray(filteredDataSource) ? filteredDataSource : []}
        rowKey="subscriptionId"
        loading={isPending}
        scroll={{ x: 1000 }}
      />
      <CancelSubscriptionModal
        visible={isCancelSubscriptionModalOpen}
        onCancel={() => {
          setIsCancelSubscriptionModalOpen(false);
          refetch();
        }}
        refetch={refetch}
        siteId={selectedSiteForCancellation?.toString() || ''}
      />
    </>
  );
};

export default Subscriptions;
